// Composant Tableau des Catégories de Scoring
import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { FiCheckCircle } from 'react-icons/fi';

export interface CategoryScore {
  category: string;
  status: 'completed' | 'pending' | 'warning';
  score: number;
}

interface ScoreCategoryTableProps {
  categories: CategoryScore[];
}

const Table = styled.div`
  width: 100%;
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;

  @media print {
    page-break-inside: avoid;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  background: ${theme.colors.primary}15;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-weight: ${theme.typography.weights.semibold};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  border-bottom: 2px solid ${theme.colors.primary}30;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.surface};
  align-items: center;
  transition: background ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface}40;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Cell = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
`;

const StatusBadge = styled.div<{ status: 'completed' | 'pending' | 'warning' }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};

  ${props => {
    switch (props.status) {
      case 'completed':
        return `
          background: ${theme.colors.success}20;
          color: ${theme.colors.success};
        `;
      case 'warning':
        return `
          background: ${theme.colors.warning}20;
          color: ${theme.colors.warning};
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text}60;
        `;
    }
  }}
`;

const ScoreCell = styled.div`
  font-weight: ${theme.typography.weights.bold};
  font-size: ${theme.typography.sizes.base};
`;

export const ScoreCategoryTable: React.FC<ScoreCategoryTableProps> = React.memo(({ categories }) => {
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Validé';
      case 'warning': return 'Attention';
      default: return 'En attente';
    }
  };

  return (
    <Table>
      <TableHeader>
        <Cell></Cell>
        <Cell style={{ textAlign: 'center' }}>Statut</Cell>
        <Cell style={{ textAlign: 'center' }}>Note</Cell>
      </TableHeader>
      {categories.map((cat, index) => (
        <TableRow key={index}>
          <Cell>{cat.category}</Cell>
          <Cell style={{ textAlign: 'center' }}>
            <StatusBadge status={cat.status}>
              <FiCheckCircle /> {getStatusLabel(cat.status)}
            </StatusBadge>
          </Cell>
          <ScoreCell style={{ textAlign: 'center' }}>{cat.score}</ScoreCell>
        </TableRow>
      ))}
    </Table>
  );
});
